import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {
  MatDialogRef,
  MatDialogContent,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatSelectChange, MatSelect } from '@angular/material/select';
import {
  NgxScannerQrcodeComponent,
  NgxScannerQrcodeModule
} from 'ngx-scanner-qrcode';
import { ToastrService } from 'ngx-toastr';
import {
  Subject,
  catchError,
  filter,
  map,
  mergeMap,
  of,
  take,
  takeUntil,
  tap
} from 'rxjs';
import { SearchService } from '../../services/search.service';
import { MatOption } from '@angular/material/core';
import { NgFor } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatButton } from '@angular/material/button';
import { RetailProductService } from '../../services/retail-product.service';
import { ConfigService } from '../../services/config.service';
import { ItemService } from '../../services/item.service';

@Component({
  selector: 'app-barcode-dialog',
  templateUrl: './dialog.component.html',
  standalone: true,
  imports: [
    MatDialogContent,
    MatButton,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    NgxScannerQrcodeModule
  ],
  providers: [RetailProductService, ItemService]
})
export class BarcodeScannerDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild(NgxScannerQrcodeComponent) scanner: NgxScannerQrcodeComponent;

  viewOtherCameras = false;
  ngUnSubscribe = new Subject<void>();
  devices = [];

  constructor(
    private searchService: SearchService,
    private retailProductService: RetailProductService,
    private toastService: ToastrService,
    private dialogRef: MatDialogRef<BarcodeScannerDialogComponent>,
    private configService: ConfigService,
    private itemService: ItemService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngAfterViewInit() {
    this.scanner.start();
    this.selectBackCamera();
    this.subscribeToDataEvents();
  }

  private subscribeToDataEvents() {
    this.scanner.data
      .asObservable()
      .pipe(
        takeUntil(this.ngUnSubscribe),
        filter((data: any[]) => data?.length > 0 && Boolean(data[0].value)),
        tap(() => this.scanner.stop()),
        mergeMap((data) => this.lookupValue(data[0].value))
      )
      .subscribe((data) => this.handleSearchResult(data));
  }

  private handleSearchResult({
    result,
    value
  }: {
    result: any;
    value: string;
  }) {
    if (result) {
      this.dialogRef.close(result);
    } else {
      this.toastService.warning(
        `${value} did not match the code/barcode of any hire items or retail products`,
        'No match found'
      );
      this.scanner.start();
    }
  }

  lookupValue(value: any) {
    if (this.data?.type === 'retailProduct') {
      return this.retailProductSearch(value);
    }

    return this.configService.currentUser.pipe(
      filter(Boolean),
      take(1),
      map((user) => user?.tenant?.shopfront_url),
      mergeMap((shopfrontUrl) => {
        console.log({ value, shopfrontUrl });
        if (value.indexOf(shopfrontUrl) > -1) {
          return this.itemService.findItemByUUID(value.split('/').pop()).pipe(
            map((item) => ({
              result: {
                resource: item,
                type: 'item'
              },
              value
            })),
            catchError(() => of({ value, result: null }))
          );
        } else {
          return this.globalSearch(value);
        }
      })
    );
  }

  private retailProductSearch(value: any) {
    return this.retailProductService.checkCode('both', value).pipe(
      map((response) => {
        return {
          result: {
            resource: response,
            type: 'retailProduct'
          },
          value
        };
      }),
      catchError(() => of({ value, result: null }))
    );
  }

  private globalSearch(value: any) {
    return this.searchService.gloabalSearch(value.trim()).pipe(
      map((results) => {
        if (results.retailProducts.length) {
          return {
            result: {
              resource: results.retailProducts[0],
              type: 'retailProduct'
            },
            value
          };
        }
        if (results.items.length) {
          return {
            result: { resource: results.items[0], type: 'item' },
            value
          };
        }
        return { result: null, value };
      })
    );
  }

  private selectBackCamera() {
    this.scanner.devices
      .asObservable()
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((devices) => {
        console.log(devices);
        this.devices = devices;
        const device = devices.find((device) =>
          device.label.toLocaleLowerCase().includes('back')
        );
        if (device) {
          this.scanner.playDevice(device.deviceId);
          this.scanner.deviceIndexActive = this.devices.indexOf(device);
        }
      });
  }

  onDeviceSelect({ value }: MatSelectChange) {
    this.scanner.playDevice(value.deviceId);
    this.scanner.deviceIndexActive = this.devices.indexOf(value);
    this.viewOtherCameras = false;
  }

  ngOnDestroy(): void {
    this.scanner.stop();
    this.ngUnSubscribe.next();
    this.ngUnSubscribe.complete();
  }
}
